import type { GraphicsProfile } from '../core/types';

/**
 * Optional, defensive graphics-preferences persistence (src/persist, AD-7). Holds
 * ONLY the two device-local, non-essential preferences: `requestedProfile` and
 * `reducedEffects`. Every read is defensive (a missing, blocked, throwing, or
 * malformed value yields the safe default, never an error) and every write may
 * silently fail. The full simulator works when storage is absent, blocked, or
 * cleared (Auto + system reduced-motion). No personal data and no behavioural
 * analytics are stored.
 *
 * `activeRenderMode` and `unavailableFeatures` are runtime-derived from the
 * current device's capabilities and MUST NOT be persisted. This module is
 * self-contained (no `platform` import): it does its own `try`/`catch` around
 * `localStorage`, mirroring `playback-pref.ts`, so it never throws regardless of
 * the storage state. It is NOT part of `AppState`: the reducer never reads or
 * writes it, so `Reset` leaves stored preferences untouched.
 */

const PROFILE_KEY = 'kepler_graphics_profile';
const REDUCED_EFFECTS_KEY = 'kepler_reduced_effects';

const VALID_PROFILES: readonly GraphicsProfile[] = ['auto', 'enhanced', 'compatibility'];

export interface GraphicsPrefs {
  readonly requestedProfile: GraphicsProfile;
  readonly reducedEffects: boolean;
}

/** The safe defaults used whenever storage is unavailable or a value is invalid. */
export const DEFAULT_GRAPHICS_PREFS: GraphicsPrefs = {
  requestedProfile: 'auto',
  reducedEffects: false
};

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

function isGraphicsProfile(value: unknown): value is GraphicsProfile {
  return typeof value === 'string' && (VALID_PROFILES as readonly string[]).includes(value);
}

/**
 * Defensive reader. Returns validated preferences, falling back to the safe
 * defaults for a missing key, an unknown `requestedProfile`, a non-boolean
 * `reducedEffects`, or throwing/blocked storage. Never throws.
 */
export function readGraphicsPrefs(): GraphicsPrefs {
  const store = storage();
  if (!store) return DEFAULT_GRAPHICS_PREFS;
  try {
    const rawProfile = store.getItem(PROFILE_KEY);
    const rawReduced = store.getItem(REDUCED_EFFECTS_KEY);
    const requestedProfile = isGraphicsProfile(rawProfile)
      ? rawProfile
      : DEFAULT_GRAPHICS_PREFS.requestedProfile;
    const reducedEffects =
      rawReduced === 'true' ? true : rawReduced === 'false' ? false : DEFAULT_GRAPHICS_PREFS.reducedEffects;
    return { requestedProfile, reducedEffects };
  } catch {
    return DEFAULT_GRAPHICS_PREFS;
  }
}

/** Defensive writer for the requested profile. Swallows any storage failure. */
export function writeGraphicsProfile(profile: GraphicsProfile): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(PROFILE_KEY, profile);
  } catch {
    // Writes may silently fail; persistence is non-load-bearing (AD-7).
  }
}

/** Defensive writer for the reduced-effects toggle. Swallows any storage failure. */
export function writeReducedEffects(value: boolean): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(REDUCED_EFFECTS_KEY, value ? 'true' : 'false');
  } catch {
    // Writes may silently fail; persistence is non-load-bearing (AD-7).
  }
}
